/** @jsx jsx */
import { jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import { Text, RichText } from '@sitecore-jss/sitecore-jss-react';
import { withNamespaces } from 'react-i18next';
import defaultsDeep from 'lodash/defaultsDeep';
import get from 'lodash/get';
import Spacer from '../../ui/Spacer';
import CtaLink from '../../ui/CtaLink';
import { types } from '../../ui/CtaLink/types';
import { sizes } from '../../ui/CtaLink/types';
import DotComponent from '../../ui/DotComponent';
import { composeStringToId } from '../../../utils/stringToId';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import { componentHasStickyLabel } from '../StickyBar';
import { Container, Column, Row } from '../../../styles/Grid';
import { keyColors, colorsPalette } from '../../../styles/colors';
import {
  shortDescriptionCss,
  solutionHeadlineCss,
  itemCss,
  headlineCss,
  spacerPaddingCss,
  spacerMarginCss,
  containerCss,
  linkCss,
} from './index.styles';

const defaultBackgroundColorKey = keyColors.brown10;

const defaultData = {
  Headline: {
    value: '',
  },

  Solutions: [],

  BackgroundColor: {
    fields: {
      Value: {
        value: defaultBackgroundColorKey,
      },
    },
  },
};

class TextAndList extends SafePureComponent {
  getDarkThemeCustomCss(isDarkTheme) {
    return isDarkTheme ? linkCss : null;
  }

  safeRender() {
    const { props } = this;
    const fields = defaultsDeep(props.fields, defaultData);
    const hasStickyLabel = componentHasStickyLabel(props);
    const t = props.t;
    const BackgroundColor =
      fields.BackgroundColor || defaultData.BackgroundColor;
    const theme = get(
      BackgroundColor,
      'fields.Value.value',
      defaultBackgroundColorKey,
    );
    const isDarkTheme = theme === keyColors.black;
    const { white, black } = colorsPalette;
    const componentName = get(props, 'rendering.componentName', '');

    const renderItems = () =>
      fields.Solutions.map((item, index) => {
        return (
          <li
            key={composeStringToId(
              get(item, 'fields.SolutionHeadline.value', ''),
              `solution-${index}`,
            )}
            css={itemCss(theme)}
          >
            <h3 css={solutionHeadlineCss}>
              <Text field={get(item, 'fields.SolutionHeadline', '')} />
            </h3>
            <div css={containerCss}>
              <RichText
                css={shortDescriptionCss}
                field={get(item, 'fields.SolutionShortDescription', '')}
              />
              <CtaLink
                {...this.getDarkThemeCustomCss(isDarkTheme)}
                href={get(item, 'fields.Url.value', '')}
                text={t('LearnMoreCTA')}
                size={sizes.small}
                type={isDarkTheme ? types.secondary : types.primary}
                componentName={componentName}
                isGTMTrackable
              />
            </div>
          </li>
        );
      });

    return (
      <Spacer
        paddingCss={spacerPaddingCss}
        marginCss={spacerMarginCss}
        backgroundColor={theme}
        containerProps={{
          'data-sticky-id': hasStickyLabel && get(props, 'rendering.uid'),
        }}
      >
        <Container
          css={{
            color: isDarkTheme ? white : black,
          }}
        >
          <Row>
            <Column cols={{ sm: 6, md: 8, lg: 12, xl: 3 }}>
              <h2 css={headlineCss}>
                <Text field={fields.Headline} />
                <DotComponent />
              </h2>
            </Column>
            <Column cols={{ sm: 6, md: 8, lg: 12, xl: 7 }} offsets={{ xl: 2 }}>
              <ul>{renderItems()}</ul>
            </Column>
          </Row>
        </Container>
      </Spacer>
    );
  }
}

TextAndList.defaultProps = { fields: defaultData, t: key => key };

TextAndList.displayName = 'TextAndList';

TextAndList.propTypes = {
  fields: PropTypes.shape({
    Headline: PropTypes.shape({
      value: PropTypes.string,
    }),

    Solutions: PropTypes.arrayOf(
      PropTypes.shape({
        fields: PropTypes.shape({
          SolutionHeadline: PropTypes.shape({
            value: PropTypes.string,
          }),
          SolutionShortDescription: PropTypes.shape({
            value: PropTypes.string,
          }),
          Url: PropTypes.shape({
            value: PropTypes.string,
          }),
        }),
      }),
    ),
  }),
  t: PropTypes.func,
};

export default withNamespaces()(TextAndList);
